// src/pages/Profile/ProfileView.tsx
import React from 'react'
import Sidebar from '../../components/Layout/Sidebar'
import Header from '../../components/Layout/Header'
import Card from '../../components/UI/Card'
import Button from '../../components/UI/Button'
import {
    User, Edit2, Save, Camera, Phone, Calendar,
    Smartphone, Mail, LogOut, Wallet, Key, Eye, EyeOff,
    CheckCircle, AlertCircle
} from 'lucide-react'

interface ProfileViewProps {
    wallets: {alfa: number; touch: number}
    user: any
    loading: boolean
    editing: boolean
    sidebarOpen: boolean
    saving: boolean
    message: any
    formData: any
    passwordData: any
    showCurrentPassword: boolean
    showNewPassword: boolean
    showConfirmPassword: boolean
    isChangingPassword: boolean
    passwordMessage: any
    toastMessage: {type: 'success' | 'error', text: string} | null  // 👈 جديد
    setEditing: (value: boolean) => void
    toggleSidebar: () => void
    handleInputChange: (e: React.ChangeEvent<HTMLInputElement>) => void
    handlePasswordChange: (e: React.ChangeEvent<HTMLInputElement>) => void
    handleSaveProfile: () => Promise<void>
    handleChangePassword: (e: React.FormEvent) => Promise<void>
    handleLogout: () => void
    setShowCurrentPassword: (value: boolean) => void
    setShowNewPassword: (value: boolean) => void
    setShowConfirmPassword: (value: boolean) => void
    getWalletType: () => string
}

export const ProfileView: React.FC<ProfileViewProps> = ({
    wallets,
    user, loading, editing, sidebarOpen, saving, message,
    formData, passwordData, showCurrentPassword, showNewPassword,
    showConfirmPassword, isChangingPassword, passwordMessage,
    toastMessage,  // 👈 جديد
    setEditing, toggleSidebar, handleInputChange, handlePasswordChange,
    handleSaveProfile, handleChangePassword, handleLogout,
    setShowCurrentPassword, setShowNewPassword, setShowConfirmPassword,
    getWalletType
}) => {
    // Add this line for debugging
    console.log('👤 ProfileView - user data:', user);
    console.log('👤 ProfileView - wallets:', wallets);

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
                <p className="mr-4 text-gray-600">جاري تحميل الملف الشخصي...</p>
            </div>
        )
    }

    return (
        <div className="min-h-screen bg-gray-50">
            <Header toggleSidebar={toggleSidebar} />

            <Sidebar
                isOpen={sidebarOpen}
                onClose={() => toggleSidebar()}
                isMobile={true}
            />

            <div className="hidden lg:block">
                <Sidebar isMobile={false} />
            </div>

            <main className="pt-16 lg:pr-64">
                <div className="p-4 lg:p-6">
                    {/* Header */}
                    <div className="flex items-center justify-between mb-6">
                        <div className="flex items-center gap-3">
                            <User className="w-6 h-6 text-blue-600" />
                            <h1 className="text-2xl font-bold text-gray-800">الملف الشخصي</h1>
                        </div>
                        <Button
                            variant={editing ? "success" : "primary"}
                            icon={editing ? <Save className="w-4 h-4" /> : <Edit2 className="w-4 h-4" />}
                            onClick={() => editing ? handleSaveProfile() : setEditing(true)}
                            loading={saving}
                        >
                            {editing ? 'حفظ' : 'تعديل'}
                        </Button>
                    </div>

                    {/* 👇 Toast Message - في نص الصفحة مش ثابت */}
                    {toastMessage && (
                        <div className="mb-4 animate-fade-in-down">
                            <div className={`px-6 py-3 rounded-lg shadow-lg flex items-center gap-3 ${toastMessage.type === 'success'
                                ? 'bg-green-500 text-white'
                                : 'bg-red-500 text-white'
                                }`}>
                                {toastMessage.type === 'success'
                                    ? <CheckCircle className="w-5 h-5" />
                                    : <AlertCircle className="w-5 h-5" />
                                }
                                <span className="font-medium">{toastMessage.text}</span>
                            </div>
                        </div>
                    )}

                    {/* رسالة النجاح/الخطأ القديمة - ممكن نشيلها بعد ما نضبط toast */}
                    {message && !toastMessage && (
                        <div className={`mb-4 p-3 rounded-lg flex items-center gap-2 ${message.type === 'success' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                            }`}>
                            {message.type === 'success'
                                ? <CheckCircle className="w-5 h-5" />
                                : <AlertCircle className="w-5 h-5" />
                            }
                            <span>{message.text}</span>
                        </div>
                    )}

                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                        {/* المعلومات الشخصية */}
                        <div className="lg:col-span-2 space-y-6">
                            <Card title="المعلومات الشخصية">
                                <div className="space-y-4">
                                    {/* رقم الهاتف */}
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            رقم الهاتف
                                        </label>
                                        <div className="relative">
                                            <Phone className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                                            <input
                                                type="text"
                                                name="phone_number"
                                                value={editing ? formData.phone_number : user?.phone_number || ''}
                                                onChange={handleInputChange}
                                                className="w-full pr-10 pl-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                dir="rtl"
                                                disabled={!editing}
                                            />
                                        </div>
                                    </div>

                                    {/* البريد الإلكتروني */}
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            البريد الإلكتروني
                                        </label>
                                        <div className="relative">
                                            <Mail className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                                            <input
                                                type="email"
                                                name="email"
                                                value={editing ? formData.email : user?.email || ''}
                                                onChange={handleInputChange}
                                                className="w-full pr-10 pl-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                dir="rtl"
                                                disabled={!editing}
                                            />
                                        </div>
                                    </div>

                                    {/* نوع المحفظة */}
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            المحفظة
                                        </label>
                                        <div className="relative">
                                            <Wallet className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                                            <input
                                                type="text"
                                                value={getWalletType()}
                                                className="w-full pr-10 pl-4 py-2 border border-gray-300 rounded-lg bg-gray-50"
                                                dir="rtl"
                                                disabled
                                            />
                                        </div>
                                    </div>

                                    {/* أرصدة المحافظ */}
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            أرصدة المحافظ
                                        </label>
                                        <div className="grid grid-cols-2 gap-3">
                                            <div className="bg-gradient-to-r from-blue-50 to-blue-100 p-3 rounded-lg">
                                                <p className="text-xs text-blue-600 font-medium">Alfa</p>
                                                <p className="text-lg font-bold text-blue-900">${wallets.alfa.toFixed(2)}</p>
                                            </div>
                                            <div className="bg-gradient-to-r from-green-50 to-green-100 p-3 rounded-lg">
                                                <p className="text-xs text-green-600 font-medium">Touch</p>
                                                <p className="text-lg font-bold text-green-900">${wallets.touch.toFixed(2)}</p>
                                            </div>
                                        </div>
                                    </div>

                                    {/* تاريخ التسجيل */}
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            تاريخ التسجيل
                                        </label>
                                        <div className="relative">
                                            <Calendar className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                                            <input
                                                type="text"
                                                value={user?.created_at ? new Date(user.created_at).toLocaleDateString('ar-LB') : ''}
                                                className="w-full pr-10 pl-4 py-2 border border-gray-300 rounded-lg bg-gray-50"
                                                dir="rtl"
                                                disabled
                                            />
                                        </div>
                                    </div>
                                </div>
                            </Card>
                        </div>

                        {/* الجانب الأيمن */}
                        <div className="space-y-6">
                            {/* صورة المستخدم */}
                            <Card>
                                <div className="text-center">
                                    <div className="w-24 h-24 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full mx-auto mb-4 flex items-center justify-center">
                                        <Camera className="w-8 h-8 text-white" />
                                    </div>
                                    <h3 className="font-semibold text-gray-800">{user?.username || 'admin'}</h3>
                                    <p className="text-sm text-gray-500 mt-1">{user?.role === 'admin' ? 'مدير النظام' : 'مستخدم'}</p>
                                    <p className="text-xs text-gray-400 mt-2">آخر نشاط: {new Date().toLocaleDateString('ar-LB')}</p>
                                </div>
                            </Card>

                            {/* إحصائيات سريعة */}
                            <Card title="إحصائيات سريعة">
                                <div className="space-y-3">
                                    <div className="flex justify-between items-center p-2 bg-gray-50 rounded-lg">
                                        <span className="text-sm text-gray-600">الرصيد الحالي</span>
                                        <span className="font-bold text-green-600">${user?.balance_usd || 0}</span>
                                    </div>
                                    <div className="flex justify-between items-center p-2 bg-gray-50 rounded-lg">
                                        <span className="text-sm text-gray-600">عدد المعاملات</span>
                                        <span className="font-bold text-blue-600">{user?.totalTransactions || 0}</span>
                                    </div>
                                    <div className="flex justify-between items-center p-2 bg-gray-50 rounded-lg">
                                        <span className="text-sm text-gray-600">نوع الجهاز</span>
                                        <span className="font-bold text-purple-600">{user?.device_type || 'web'}</span>
                                    </div>
                                </div>
                            </Card>

                            {/* زر تسجيل الخروج */}
                            <Button
                                variant="danger"
                                icon={<LogOut className="w-4 h-4" />}
                                onClick={handleLogout}
                                fullWidth
                            >
                                تسجيل خروج
                            </Button>
                        </div>
                    </div>

                    {/* تغيير كلمة المرور */}
                    <div className="mt-6">
                        <Card title="تغيير كلمة المرور" subtitle="قم بتغيير كلمة المرور الخاصة بك">
                            <form onSubmit={handleChangePassword} className="space-y-4">
                                {/* كلمة المرور الحالية */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        كلمة المرور الحالية
                                    </label>
                                    <div className="relative">
                                        <Key className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                                        <input
                                            type={showCurrentPassword ? "text" : "password"}
                                            name="currentPassword"
                                            value={passwordData.currentPassword}
                                            onChange={handlePasswordChange}
                                            className="w-full pr-10 pl-10 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                            dir="rtl"
                                            placeholder="أدخل كلمة المرور الحالية"
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                                            className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                        >
                                            {showCurrentPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                                        </button>
                                    </div>
                                </div>

                                {/* كلمة المرور الجديدة */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        كلمة المرور الجديدة
                                    </label>
                                    <div className="relative">
                                        <Key className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                                        <input
                                            type={showNewPassword ? "text" : "password"}
                                            name="newPassword"
                                            value={passwordData.newPassword}
                                            onChange={handlePasswordChange}
                                            className="w-full pr-10 pl-10 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                            dir="rtl"
                                            placeholder="أدخل كلمة المرور الجديدة"
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setShowNewPassword(!showNewPassword)}
                                            className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                        >
                                            {showNewPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                                        </button>
                                    </div>
                                </div>

                                {/* تأكيد كلمة المرور */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        تأكيد كلمة المرور
                                    </label>
                                    <div className="relative">
                                        <Key className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                                        <input
                                            type={showConfirmPassword ? "text" : "password"}
                                            name="confirmPassword"
                                            value={passwordData.confirmPassword}
                                            onChange={handlePasswordChange}
                                            className="w-full pr-10 pl-10 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                            dir="rtl"
                                            placeholder="أعد إدخال كلمة المرور الجديدة"
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                            className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                        >
                                            {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                                        </button>
                                    </div>
                                </div>

                                {/* رسالة الخطأ/النجاح - قديمة */}
                                {passwordMessage && !toastMessage && (
                                    <div className={`p-3 rounded-lg flex items-center gap-2 ${passwordMessage.type === 'success' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                                        }`}>
                                        {passwordMessage.type === 'success'
                                            ? <CheckCircle className="w-5 h-5" />
                                            : <AlertCircle className="w-5 h-5" />
                                        }
                                        <span className="text-sm">{passwordMessage.text}</span>
                                    </div>
                                )}

                                <Button
                                    type="submit"
                                    variant="primary"
                                    loading={isChangingPassword}
                                    fullWidth
                                >
                                    تغيير كلمة المرور
                                </Button>
                            </form>
                        </Card>
                    </div>
                </div>
            </main>
        </div>
    )
}